import {noop} from '@benchling/js-utils';
import {createStyles, CSSObject} from '@mantine/core';
import React, {useState} from 'react';
import {Handle, HandleType} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import {
  ADD_NODE_HANDLE_OUTER_REACH,
  ControlledAddNodeHandle,
} from 'ui-library/reactflow/handles/AddNodeHandle';
import HandleDot from 'ui-library/reactflow/handles/HandleDot';
import {defaultHandleProps} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeHandleUtils';

import type {UnitOperationStatusType} from '../../procedureRunOverviewUtils';
import {
  canDrawOutgoingEdgeFromStatus,
  type ConnectionRole,
} from './procedureRunDesignFlowchartConnectionUtils';
import {useProcedureRunDesignFlowchartIsEditable} from './procedureRunDesignFlowchartEditableContext';

/**
 * Visual variant for a grid node's flush handle. `subtle` renders a transparent handle. Add a
 * variant here and flip the `HANDLE_VARIANT_SHOWS_DOT` lookup to introduce new handle styles.
 */
export type ProcedureRunDesignFlowchartNodeHandleVariant = 'subtle';

const DEFAULT_HANDLE_VARIANT: ProcedureRunDesignFlowchartNodeHandleVariant = 'subtle';

/** Whether a given handle variant renders a visible dot. */
const HANDLE_VARIANT_SHOWS_DOT: Record<ProcedureRunDesignFlowchartNodeHandleVariant, boolean> = {
  subtle: false,
};

/** Deterministic handle id for a grid node, so edges can target handles without rendering first. */
export function getProcedureRunDesignFlowchartNodeHandleId(nodeId: string, handleType: HandleType): string {
  return `${nodeId}-${handleType}`;
}

/**
 * Handle id for the floating blue arrow handle, kept distinct from the flush edge-anchor id
 * so edges stay attached to the flush handle while this arrow handle floats on top.
 */
function getActiveLayerConnectionHandleId(nodeId: string, handleType: HandleType): string {
  return `${getProcedureRunDesignFlowchartNodeHandleId(nodeId, handleType)}-add`;
}

/** Hides a mounted handle so its geometry stays stable while it is not revealed. */
const HIDDEN_HANDLE_STYLE: React.CSSProperties = {visibility: 'hidden', pointerEvents: 'none'};

/**
 * Left `target` / right `source` handle, flush and invisible (1px) so edges sit tight against the
 * node edge. It is the permanent edge anchor for every node and slot, including active-layer source
 * nodes where it renders alongside the floating arrow handle.
 */
function FlushNodeHandle({
  nodeId,
  handleType,
  variant,
}: {
  nodeId: string;
  handleType: HandleType;
  variant: ProcedureRunDesignFlowchartNodeHandleVariant;
}): JSX.Element {
  const {classes} = useHandleStyles();
  return (
    <Handle
      {...defaultHandleProps(handleType)}
      id={getProcedureRunDesignFlowchartNodeHandleId(nodeId, handleType)}
      isConnectable={false}
      className={classes.handle}
      {...getTestProps({component: 'FlushNodeHandle', element: handleType})}
    >
      {HANDLE_VARIANT_SHOWS_DOT[variant] && <HandleDot />}
    </Handle>
  );
}

/**
 * The interactive blue add-node handle for an active-layer source connection. It floats beside the
 * node, alongside the flush handle: the flush handle keeps the canonical edge-anchor id while this
 * handle uses a distinct `-add` id, so the edge stays tight to the node and the arrow handle floats on
 * top of it.
 *
 * The handle stays mounted but the handle is hidden at rest, showing nothing (not even a resting
 * dot): at rest it's just the bare flush edge. The blue arrow handle appears on hover, or, when an
 * active node is still disconnected (`showPersistentSourceHandle`), persists independent of hover as
 * a heads-up. Persistence is display-only: the handle is connectable whenever it renders, so split
 * and pool edges can still be drawn after everything is connected.
 */
function ActiveLayerConnectionHandle({
  nodeId,
  handleType,
  showPersistentSourceHandle,
  displayAsHovered,
  isConnectable,
}: {
  nodeId: string;
  handleType: HandleType;
  showPersistentSourceHandle: boolean;
  displayAsHovered: boolean;
  isConnectable: boolean;
}): JSX.Element {
  const [isHandleHovered, setIsHandleHovered] = useState(false);
  const shouldShow = displayAsHovered || isHandleHovered || showPersistentSourceHandle;
  return (
    // Hide with `opacity` so the handle stays mounted and hit-testable at rest.
    <div style={{opacity: shouldShow ? 1 : 0}}>
      <ControlledAddNodeHandle
        id={getActiveLayerConnectionHandleId(nodeId, handleType)}
        type={handleType}
        isConnectable={isConnectable}
        // Drag-to-connect originates from the handle itself while eligible, so the click is a no-op.
        // Passing `onClick` is also what makes `AddNodeHandle` render the arrow, so it must stay provided.
        onClick={noop}
        displayHandleAsHovered={shouldShow}
        setDisplayHandleAsHovered={setIsHandleHovered}
      />
    </div>
  );
}

/**
 * How a source node's draw affordance should render: `draw` mounts the floating blue arrow so an
 * outgoing edge can be drawn from the node, `none` renders only the flush edge anchor. New source-side
 * rules (e.g. a future read-only affordance) compose into {@link getSourceHandleAffordance} rather than
 * being threaded through this component as separate booleans.
 */
export type SourceHandleAffordance = 'none' | 'draw';

/**
 * Derives a source node's draw affordance from its status. This is the single place source-side draw
 * rules compose, so the handle component stays a pure renderer of the resulting affordance.
 */
export function getSourceHandleAffordance(
  status: UnitOperationStatusType | undefined
): SourceHandleAffordance {
  return canDrawOutgoingEdgeFromStatus(status) ? 'draw' : 'none';
}

interface ProcedureRunDesignFlowchartNodeHandleProps {
  nodeId: string;
  handleType: HandleType;
  connectionRole: ConnectionRole;
  // Only consulted for the `source` slot. The `target` slot passes `false`.
  showPersistentSourceHandle: boolean;
  isNodeHovered: boolean;
  // Only consulted for the `source` slot. The `target` slot omits it (defaults to `none`).
  sourceAffordance?: SourceHandleAffordance;
  variant?: ProcedureRunDesignFlowchartNodeHandleVariant;
}

/**
 * The white target connection dot for an active-layer target node. It reuses the standard Benchling
 * node connection dot, a white dot with a gray outline, sitting tight against the node's left edge
 * rather than the larger add-node affordance.
 *
 * It stays mounted, so its geometry is stable and incoming edges keep their anchor on the flush
 * handle, and is revealed only while the node is hovered. It accepts drawn connections when
 * `isConnectable`, so a drag from an upstream source can land on it.
 */
function ActiveLayerTargetHandle({
  nodeId,
  visible,
  isConnectable,
}: {
  nodeId: string;
  visible: boolean;
  isConnectable: boolean;
}): JSX.Element {
  return (
    <span
      style={visible ? undefined : HIDDEN_HANDLE_STYLE}
      {...getTestProps({
        component: 'ActiveLayerTargetHandle',
        element: visible ? 'visible' : 'hidden',
      })}
    >
      <Handle
        {...defaultHandleProps('target')}
        id={getActiveLayerConnectionHandleId(nodeId, 'target')}
        isConnectable={isConnectable}
        {...getTestProps({component: 'ActiveLayerTargetHandle', element: 'root'})}
      >
        <HandleDot />
      </Handle>
    </span>
  );
}

/**
 * A grid node's handle for one slot (source or target), chosen by the node's connection role. An
 * upstream source slot adds a floating blue arrow on hover, an active-layer target slot adds a white
 * target dot on hover while editing, and every other slot renders just the flush handle. The flush
 * handle is always the edge anchor, so the edge stays tight to the node whether or not an affordance
 * shows.
 *
 * Memoized so frequent node-body re-renders (status, highlight, selection) don't re-render the handle.
 * Hover state still flows through to surface the affordance on hover.
 */
function ProcedureRunDesignFlowchartNodeHandleComponent({
  nodeId,
  handleType,
  connectionRole,
  showPersistentSourceHandle,
  isNodeHovered,
  sourceAffordance = 'none',
  variant = DEFAULT_HANDLE_VARIANT,
}: ProcedureRunDesignFlowchartNodeHandleProps): JSX.Element {
  const isEditable = useProcedureRunDesignFlowchartIsEditable();
  const flushHandle = <FlushNodeHandle nodeId={nodeId} handleType={handleType} variant={variant} />;
  // Drawing connections is an edit-mode action, so the source arrow only renders while editing. The
  // flush handle still renders in every mode, so edges stay anchored tight to the node.
  if (handleType === 'source' && connectionRole === 'source' && isEditable && sourceAffordance === 'draw') {
    // The flush handle anchors edges tight to the node. The floating arrow shows on hover for every
    // upstream source. `showPersistentSourceHandle` only keeps the arrow shown at rest as a
    // disconnected heads-up. The handle stays connectable regardless, so split and pool edges can be
    // drawn even after every active node is connected.
    return (
      <>
        {flushHandle}
        <ActiveLayerConnectionHandle
          nodeId={nodeId}
          handleType="source"
          showPersistentSourceHandle={showPersistentSourceHandle}
          displayAsHovered={isNodeHovered}
          isConnectable={true}
        />
      </>
    );
  }
  if (handleType === 'target' && connectionRole === 'target' && isEditable) {
    // The flush handle anchors edges tight to the node. The white target dot is an editing affordance
    // shown only on hover while editing. It stays mounted so its geometry is stable and incoming edges
    // keep their anchor, and it accepts drawn connections so a drag from an upstream source can land.
    return (
      <>
        {flushHandle}
        <ActiveLayerTargetHandle nodeId={nodeId} visible={isNodeHovered} isConnectable={true} />
      </>
    );
  }
  return flushHandle;
}

export const ProcedureRunDesignFlowchartNodeHandle = React.memo(
  ProcedureRunDesignFlowchartNodeHandleComponent
);

/**
 * Transparent hover gutters that widen a node's hit area by one handle-reach
 * on each side, full height, so the cursor can travel from the node body out
 * to a floating connection handle without causing the node to lose hover and flicker.
 */
export function ProcedureRunDesignFlowchartNodeHoverGutters(): JSX.Element {
  const {classes} = useGutterStyles();
  return (
    <>
      <span
        aria-hidden={true}
        className={classes.leftGutter}
        {...getTestProps({component: 'ProcedureRunDesignFlowchartNodeHoverGutter', element: 'left'})}
      />
      <span
        aria-hidden={true}
        className={classes.rightGutter}
        {...getTestProps({component: 'ProcedureRunDesignFlowchartNodeHoverGutter', element: 'right'})}
      />
    </>
  );
}

const useHandleStyles = createStyles(() => ({
  handle: {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    opacity: 0,
    pointerEvents: 'none',
  },
}));

const useGutterStyles = createStyles(() => {
  // Transparent reach region; must accept pointer events so hovering it keeps the node hovered.
  const gutter: CSSObject = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: ADD_NODE_HANDLE_OUTER_REACH,
    pointerEvents: 'auto',
  };
  return {
    leftGutter: {...gutter, left: -ADD_NODE_HANDLE_OUTER_REACH},
    rightGutter: {...gutter, right: -ADD_NODE_HANDLE_OUTER_REACH},
  };
});
